<template>
    <div class="p-6 text-left">
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-col justify-center items-center py-12">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
            <p class="mt-4 text-gray-600 text-lg">Loading your response...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <font-awesome-icon icon="exclamation-circle" class="text-3xl text-red-500" />
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">Error Loading Response</h3>
            <p class="text-gray-600">{{ error }}</p>
        </div>

        <!-- Response View -->
        <div v-else-if="survey && userResponse" class="space-y-6">
            <!-- Survey Header -->
            <div class="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
                <div class="flex items-start justify-between">
                    <div class="flex-1">
                        <h2 class="text-2xl font-bold mb-2">{{ survey.title }}</h2>
                        <div v-if="survey.description" class="text-green-100 text-sm survey-description"
                            v-html="survey.description"></div>
                    </div>
                </div>
                <div class="mt-4 pt-4 border-t border-green-400">
                    <div class="flex items-center space-x-2 text-green-100">
                        <font-awesome-icon icon="calendar-check" />
                        <span class="text-sm font-medium">
                            Submitted on {{ formatDate(userResponse.submitted_at) }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Response Answers -->
            <div class="space-y-4">
                <div v-for="(question, index) in sortedQuestions" :key="question.id"
                    class="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                    <div class="flex items-start space-x-4">
                        <!-- Question Number Badge -->
                        <div class="flex-shrink-0">
                            <div
                                class="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-md">
                                <span class="text-white font-bold text-sm">Q{{ index + 1 }}</span>
                            </div>
                        </div>

                        <!-- Question Content -->
                        <div class="flex-1 min-w-0">
                            <p class="text-base font-semibold text-gray-900 mb-3 flex items-center">
                                <font-awesome-icon icon="question-circle" class="mr-2 text-blue-600" />
                                {{ question.question_text }}
                            </p>

                            <!-- Answer Display -->
                            <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <div v-for="answer in getUserAnswerForQuestion(question.id)" :key="answer.id">
                                    <!-- Emoji Rating Answer -->
                                    <div v-if="question.type === 'emoji_rating' && answer.answer_text"
                                        class="flex items-center justify-center">
                                        <img :src="getEmojiRatingSvg(answer.answer_text)"
                                            :alt="getEmojiRatingLabel(answer.answer_text)"
                                            class="w-16 h-16 sm:w-20 sm:h-20 object-contain" />
                                    </div>

                                    <!-- Star Rating Answer -->
                                    <div v-else-if="question.type === 'rating' && answer.answer_text"
                                        class="flex items-center justify-center">
                                        <div class="flex items-center space-x-1">
                                            <span v-for="star in 5" :key="star" class="text-3xl sm:text-4xl"
                                                :class="star <= parseInt(answer.answer_text) ? 'text-yellow-400' : 'text-gray-300'">
                                                ⭐
                                            </span>
                                        </div>
                                    </div>

                                    <!-- Signature Answer -->
                                    <div v-else-if="question.type === 'signature' && answer.answer_text" class="flex items-center gap-2 flex-wrap">
                                        <img :src="answer.answer_text" alt="Signature" class="max-w-xs border border-gray-300 rounded-lg shadow-sm" />
                                        <button @click="downloadSignature(answer.answer_text, question.question_text)"
                                            class="flex items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer">
                                            <font-awesome-icon icon="download" class="mr-2" />
                                            Download
                                        </button>
                                    </div>

                                    <!-- File Upload Answer -->
                                    <div v-else-if="question.type === 'file_upload' && answer.answer_text" class="flex items-center gap-2 flex-wrap">
                                        <span class="text-sm text-gray-700 font-medium mr-2">{{ getSurveyFileName(answer.answer_text) }}</span>
                                        <div class="flex space-x-2">
                                            <button @click="viewSurveyFile(answer.answer_text)"
                                                class="flex items-center justify-center bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer">
                                                <font-awesome-icon icon="eye" class="mr-2" />
                                                View
                                            </button>
                                            <button @click="downloadSurveyFile(answer.answer_text, getSurveyFileName(answer.answer_text))"
                                                class="flex items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer">
                                                <font-awesome-icon icon="download" class="mr-2" />
                                                Download
                                            </button>
                                        </div>
                                    </div>

                                    <!-- Grid Single Select Answer -->
                                    <div v-else-if="question.type === 'grid_single_select' && answer.answer_text"
                                        class="overflow-x-auto -mx-1">
                                        <table class="min-w-full border border-gray-300 rounded-lg">
                                            <thead>
                                                <tr class="bg-gray-50">
                                                    <!-- Blank cell for row 0, column 0 -->
                                                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300 border-r border-gray-300"></th>
                                                    <!-- Column headers (row 0, columns 1+) -->
                                                    <th v-for="(col, colIndex) in sortedOptions(question.options)" :key="colIndex"
                                                        class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300 border-r border-gray-300 last:border-r-0">
                                                        {{ col.option_text }}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <!-- Data rows (rows 1+) -->
                                                <tr v-for="(rowTitle, rowIndex) in (question.grid_row_titles || [])" :key="rowIndex"
                                                    class="border-b border-gray-200 hover:bg-gray-50">
                                                    <!-- Row title (column 0) -->
                                                    <td class="px-4 py-3 text-sm font-semibold text-gray-700 border-r border-gray-200">
                                                        {{ rowTitle }}
                                                    </td>
                                                    <!-- Display selected column for each row -->
                                                    <td v-for="(col, colIndex) in sortedOptions(question.options)" :key="colIndex"
                                                        class="px-4 py-3 text-gray-800 border-r border-gray-200 last:border-r-0 text-center">
                                                        <span v-if="parseGridAnswer(answer.answer_text)[rowIndex] == col.id" 
                                                            class="inline-flex items-center justify-center w-6 h-6 bg-blue-500 text-white rounded-full text-xs font-semibold">
                                                            ✓
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <!-- Grid Multiple Select Answer -->
                                    <div v-else-if="question.type === 'grid_multiple_select' && answer.answer_text"
                                        class="overflow-x-auto -mx-1">
                                        <table class="min-w-full border border-gray-300 rounded-lg">
                                            <thead>
                                                <tr class="bg-gray-50">
                                                    <!-- Blank cell for row 0, column 0 -->
                                                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300 border-r border-gray-300"></th>
                                                    <!-- Column headers (row 0, columns 1+) -->
                                                    <th v-for="(col, colIndex) in sortedOptions(question.options)" :key="colIndex"
                                                        class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300 border-r border-gray-300 last:border-r-0">
                                                        {{ col.option_text }}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <!-- Data rows (rows 1+) -->
                                                <tr v-for="(rowTitle, rowIndex) in (question.grid_row_titles || [])" :key="rowIndex"
                                                    class="border-b border-gray-200 hover:bg-gray-50">
                                                    <!-- Row title (column 0) -->
                                                    <td class="px-4 py-3 text-sm font-semibold text-gray-700 border-r border-gray-200">
                                                        {{ rowTitle }}
                                                    </td>
                                                    <!-- Display selected columns for each row (multiple selections) -->
                                                    <td v-for="(col, colIndex) in sortedOptions(question.options)" :key="colIndex"
                                                        class="px-4 py-3 text-gray-800 border-r border-gray-200 last:border-r-0 text-center">
                                                        <span v-if="parseGridAnswer(answer.answer_text)[rowIndex] && Array.isArray(parseGridAnswer(answer.answer_text)[rowIndex]) && parseGridAnswer(answer.answer_text)[rowIndex].includes(col.id)" 
                                                            class="inline-flex items-center justify-center w-6 h-6 bg-blue-500 text-white rounded text-xs font-semibold">
                                                            ✓
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <!-- Switch Answer (show label instead of 0/1) -->
                                    <p v-else-if="question.type === 'switch'" class="text-gray-800 font-medium">
                                        {{ getSwitchAnswerLabel(question, answer.answer_text) }}
                                    </p>

                                    <!-- Text/Textarea Answer -->
                                    <p v-else-if="answer.answer_text && question.type !== 'emoji_rating' && question.type !== 'rating' && question.type !== 'file_upload' && question.type !== 'signature' && question.type !== 'grid_single_select' && question.type !== 'grid_multiple_select' && question.type !== 'switch' && !answer.option_id && (!answer.option_ids || answer.option_ids.length === 0)"
                                        class="text-gray-800 font-medium whitespace-pre-wrap">
                                        {{ answer.answer_text }}
                                    </p>

                                    <!-- Single Option Answer (Radio/Select) -->
                                    <div v-else-if="answer.option_id" class="flex items-center space-x-2">
                                        <span class="w-2 h-2 bg-green-500 rounded-full"></span>
                                        <span class="text-gray-800 font-medium">
                                            {{ getOptionText(answer.option_id, question) }}
                                        </span>
                                    </div>

                                    <!-- Multiple Options Answer (Checkbox) -->
                                    <div v-else-if="answer.option_ids && answer.option_ids.length > 0"
                                        class="space-y-2">
                                        <div v-for="optionId in answer.option_ids" :key="optionId"
                                            class="flex items-center space-x-2">
                                            <span class="w-2 h-2 bg-green-500 rounded-full"></span>
                                            <span class="text-gray-800 font-medium">
                                                {{ getOptionText(optionId, question) }}
                                            </span>
                                        </div>
                                    </div>

                                    <!-- No answer -->
                                    <p v-else class="text-gray-400 italic">No answer provided</p>
                                </div>
                                <p v-if="!getUserAnswerForQuestion(question.id).length" class="text-gray-400 italic">
                                    No answer provided
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Preview Modal -->
        <base-modal v-if="showPreviewModal" :title="previewModalTitle" @close="closePreviewModal" size="large">
            <div class="preview-container flex flex-col h-full">
                <!-- Loading State -->
                <div v-if="previewLoading" class="flex justify-center items-center h-full">
                    <base-spinner type="spin" class-list="h-12"></base-spinner>
                </div>

                <!-- Error State -->
                <div v-else-if="previewError" class="text-center py-8">
                    <font-awesome-icon icon="exclamation-triangle" class="text-red-500 text-4xl mb-4" />
                    <p class="text-red-600 mb-4">{{ previewError }}</p>
                    <base-button @click="downloadSurveyFile(previewFilePath, previewFileName)" class="bg-blue-600 hover:bg-blue-700 text-white">
                        <font-awesome-icon icon="download" class="mr-2" />
                        Download Instead
                    </base-button>
                </div>

                <!-- Preview Content -->
                <div v-else class="preview-content flex-1 overflow-y-auto">
                    <!-- PDF Preview -->
                    <div v-if="isPdfFile(previewFileType)" class="pdf-preview">
                        <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg" frameborder="0"></iframe>
                    </div>

                    <!-- Image Preview -->
                    <div v-else-if="isImageFile(previewFileType)" class="image-preview text-center">
                        <img :src="previewUrl" :alt="previewFileName"
                            class="max-w-full max-h-96 mx-auto rounded-lg shadow-lg" />
                    </div>

                    <!-- Text File Preview -->
                    <div v-else-if="isTextFile(previewFileType)" class="text-preview">
                        <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg" frameborder="0"></iframe>
                    </div>

                    <!-- DOCX and Office File Preview -->
                    <div v-else-if="isDocxFile(previewFileType) || isOfficeDocument(previewFileType)"
                        class="office-preview">
                        <!-- Microsoft Office Online Viewer (First Priority) -->
                        <div v-if="previewUrl && !useMammothFallback">
                            <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg" frameborder="0"></iframe>
                        </div>

                        <!-- Mammoth HTML Conversion (Fallback for DOCX) -->
                        <div v-else-if="useMammothFallback && previewHtml && isDocxFile(previewFileType)" class="p-6">
                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                <div class="flex items-start">
                                    <font-awesome-icon icon="info-circle" class="text-blue-500 text-lg mt-0.5 mr-3 flex-shrink-0" />
                                    <div class="text-sm text-blue-800">
                                        <p class="font-medium mb-1">Using HTML Preview</p>
                                        <p>Microsoft Office Online viewer is not available. Showing converted HTML preview. Some formatting
                                            may differ from the original document.</p>
                                    </div>
                                </div>
                            </div>
                            <div class="docx-wrapper mx-4">
                                <div v-html="previewHtml" class="docx-content prose max-w-none"></div>
                            </div>
                        </div>

                        <!-- Loading State -->
                        <div v-else class="text-center py-8">
                            <font-awesome-icon :icon="getFileIcon(previewFileType)"
                                :class="getFileIconColor(previewFileType)" class="text-4xl mb-4" />
                            <p class="text-gray-600">Loading document preview...</p>
                        </div>
                    </div>

                    <!-- Fallback for other file types -->
                    <div v-else class="fallback-preview text-center py-8">
                        <font-awesome-icon :icon="getFileIcon(previewFileType)"
                            :class="getFileIconColor(previewFileType)" class="text-6xl mb-4" />
                        <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ previewFileName }}</h3>
                        <p class="text-gray-600 mb-4">
                            Preview not available for this file type. Please download to view the content.
                        </p>
                        <div class="flex justify-center space-x-4">
                            <base-button @click="downloadSurveyFile(previewFilePath, previewFileName)" class="bg-blue-600 hover:bg-blue-700 text-white">
                                <font-awesome-icon icon="download" class="mr-2" />
                                Download File
                            </base-button>
                            <base-button @click="closePreviewModal" class="bg-gray-600 hover:bg-gray-700 text-white">
                                Close
                            </base-button>
                        </div>
                    </div>
                </div>

                <!-- Modal Footer - Fixed at bottom -->
                <div v-if="!previewLoading && !previewError"
                    class="flex justify-end pt-4 pr-8 border-t border-gray-200 bg-white">
                    <base-button @click="downloadSurveyFile(previewFilePath, previewFileName)" class="bg-blue-600 hover:bg-blue-700 text-white">
                        <font-awesome-icon icon="download" class="mr-2" />
                        Download
                    </base-button>
                </div>
            </div>
        </base-modal>
    </div>
</template>

<script>
import { gql } from '@apollo/client/core';
import mammoth from 'mammoth';
import BaseModal from '@/components/ui/BaseModal.vue';

const GET_MY_SURVEY_RESPONSES = gql`
  query GetMySurveyResponses($first: Int!, $page: Int!) {
    mySurveyResponses(first: $first, page: $page) {
      error
      error_code
      message
      data {
        id
        submitted_at
        survey {
          id
          title
          display_title
          description
          slug
          questions {
            id
            question_text
            type
            order
            grid_row_titles
            switch_left_label
            switch_right_label
            options {
              id
              option_text
              value
              order
            }
          }
        }
        answers {
          id
          question_id
          answer_text
          option_id
          option_ids
          question {
            id
            question_text
            type
            grid_row_titles
            options {
              id
              option_text
            }
          }
          option {
            id
            option_text
          }
        }
      }
    }
  }
`;

const GET_SURVEY_BY_SLUG = gql`
  query GetSurveyBySlug($slug: String!, $forParticipation: Boolean) {
    surveyBySlug(slug: $slug, forParticipation: $forParticipation) {
      error
      error_code
      message
      data {
        id
        title
        display_title
        description
        slug
        thumbnail_path
        thumbnail_url
        questions {
          id
          question_text
          type
          order
          switch_left_label
          switch_right_label
          options {
            id
            option_text
            value
            order
          }
        }
      }
    }
  }
`;

export default {
    name: 'ViewSubmittedResponse',
    components: {
        BaseModal,
    },
    props: {
        responseId: {
            type: [Number, String],
            required: true,
        },
        surveySlug: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            survey: null,
            userResponse: null,
            loading: true,
            error: null,
            showPreviewModal: false,
            previewFilePath: null,
            previewFileName: null,
            previewFileType: null,
            previewUrl: null,
            previewHtml: null,
            previewLoading: false,
            previewError: null,
            useMammothFallback: false,
        };
    },
    computed: {
        sortedQuestions() {
            if (!this.survey || !this.survey.questions) return [];
            return [...this.survey.questions].sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        emojiRatingOptions() {
            return {
                '1': { svg: require('@/assets/images/survey/emoji_rating/1.svg'), label: 'Very Sad' },
                '2': { svg: require('@/assets/images/survey/emoji_rating/2.svg'), label: 'Sad' },
                '3': { svg: require('@/assets/images/survey/emoji_rating/3.svg'), label: 'Neutral' },
                '4': { svg: require('@/assets/images/survey/emoji_rating/4.svg'), label: 'Happy' },
                '5': { svg: require('@/assets/images/survey/emoji_rating/5.svg'), label: 'Very Happy' },
            };
        },
        previewModalTitle() {
            return this.previewFileName ? `Preview: ${this.previewFileName}` : 'Preview File';
        },
    },
    created() {
        this.loadResponse();
    },
    methods: {
        async loadResponse() {
            this.loading = true;
            this.error = null;

            try {
                // Fetch user responses to find the one we need
                const { data } = await this.$apollo.query({
                    query: GET_MY_SURVEY_RESPONSES,
                    variables: {
                        first: 100,
                        page: 1,
                    },
                    fetchPolicy: 'network-only',
                });

                if (data.mySurveyResponses.error) {
                    this.error = data.mySurveyResponses.message || 'Failed to load response.';
                    return;
                }

                // Find the specific response (handle both string and number IDs)
                const responseIdStr = String(this.responseId);
                const response = data.mySurveyResponses.data.find(r => String(r.id) === responseIdStr);

                if (!response) {
                    this.error = 'Response not found.';
                    return;
                }

                // Verify the response belongs to the survey slug
                if (response.survey && response.survey.slug !== this.surveySlug) {
                    this.error = 'Response does not match the survey.';
                    return;
                }

                this.userResponse = response;

                // Load the survey details
                const surveyData = await this.$apollo.query({
                    query: GET_SURVEY_BY_SLUG,
                    variables: { slug: this.surveySlug, forParticipation: true },
                    fetchPolicy: 'network-only',
                });

                if (!surveyData.data || !surveyData.data.surveyBySlug) {
                    this.error = 'Failed to load survey.';
                    return;
                }

                const result = surveyData.data.surveyBySlug;

                if (result.error) {
                    this.error = result.message || 'Failed to load survey.';
                    return;
                }

                if (!result.data) {
                    this.error = 'Survey not found.';
                    return;
                }

                this.survey = result.data;
            } catch (error) {
                console.error(error);
                let errorMessage = 'Failed to load response. Please try again later.';
                if (error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message) {
                    errorMessage = error.graphQLErrors[0].message;
                } else if (error.networkError && error.networkError.message) {
                    errorMessage = error.networkError.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                this.error = errorMessage;
            } finally {
                this.loading = false;
            }
        },
        getUserAnswerForQuestion(questionId) {
            if (!this.userResponse || !this.userResponse.answers) return [];
            return this.userResponse.answers.filter(answer => answer.question_id === questionId.toString());
        },
        getOptionText(optionId, question) {
            if (!question.options) return 'Unknown option';
            const option = question.options.find(opt => opt.id === optionId.toString());
            return option ? option.option_text : 'Unknown option';
        },
        getSwitchAnswerLabel(question, answerText) {
            if (!question || question.type !== 'switch') return answerText != null ? answerText : '';
            const v = answerText != null && answerText !== '' ? String(answerText).trim() : null;
            if (v === '1') return question.switch_right_label || 'On';
            if (v === '0') return question.switch_left_label || 'Off';
            return answerText != null ? answerText : '';
        },
        sortedOptions(options) {
            if (!options) return [];
            return [...options].sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        parseGridAnswer(answerText) {
            try {
                const parsed = JSON.parse(answerText);
                if (parsed && typeof parsed === 'object') {
                    return parsed;
                }
                return {};
            } catch (e) {
                return {};
            }
        },
        getSurveyFileUrl(path) {
            if (!path) return '';
            const p = path.toString().trim();
            if (p.indexOf('http://') === 0 || p.indexOf('https://') === 0) return p;
            const base = process.env.VUE_APP_PUBLIC_URL || '';
            return `${base}/storage/${p}`;
        },
        getSurveyFileName(path) {
            if (!path) return 'Download file';
            const p = path.toString().trim();
            const parts = p.split('/');
            const last = parts[parts.length - 1] || '';
            const afterUuid = last.indexOf('_') !== -1 ? last.substring(last.indexOf('_') + 1) : last;
            return afterUuid || 'Download file';
        },
        async viewSurveyFile(path) {
            this.previewFilePath = path;
            this.previewFileName = this.getSurveyFileName(path);
            this.previewLoading = true;
            this.previewError = null;
            this.previewUrl = null;
            this.previewHtml = null;
            this.useMammothFallback = false;
            this.showPreviewModal = true;

            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation DownloadSurveyFile($path: String!) {
                            downloadSurveyFile(path: $path) {
                                error
                                error_code
                                message
                                data {
                                    download_url
                                }
                            }
                        }
                    `,
                    variables: {
                        path: path
                    }
                });

                if (!response.data.downloadSurveyFile.error) {
                    const downloadUrl = response.data.downloadSurveyFile.data.download_url;

                    // Get file type
                    const contentResponse = await this.$apollo.mutate({
                        mutation: gql`
                            mutation GetSurveyFileContent($path: String!) {
                                getSurveyFileContent(path: $path) {
                                    error
                                    error_code
                                    message
                                    data {
                                        file_type
                                    }
                                }
                            }
                        `,
                        variables: {
                            path: path
                        }
                    });

                    if (!contentResponse.data.getSurveyFileContent.error) {
                        this.previewFileType = contentResponse.data.getSurveyFileContent.data.file_type;
                    }

                    // Handle DOCX and other Office files
                    if (this.isDocxFile(this.previewFileType) || this.isOfficeDocument(this.previewFileType)) {
                        try {
                            const encodedUrl = encodeURIComponent(downloadUrl);
                            const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
                            this.previewUrl = officeViewerUrl;
                        } catch (officeError) {
                            console.error('Error setting up Office Online viewer:', officeError);
                            if (this.isDocxFile(this.previewFileType)) {
                                try {
                                    const conversionResult = await this.convertDocxToHtml(path);
                                    this.previewHtml = conversionResult.html;
                                    this.useMammothFallback = true;
                                    this.previewUrl = null;
                                } catch (conversionError) {
                                    console.error('Error converting DOCX:', conversionError);
                                    this.previewError = 'Failed to load document preview. Please download the file instead.';
                                }
                            } else {
                                this.previewUrl = downloadUrl;
                            }
                        }
                    } else {
                        this.previewUrl = downloadUrl;
                    }
                } else {
                    this.previewError = response.data.downloadSurveyFile.message;
                }
            } catch (error) {
                console.error('Error loading file for preview:', error);
                this.previewError = 'Failed to load the file for preview. Please try again.';
            } finally {
                this.previewLoading = false;
            }
        },
        closePreviewModal() {
            this.showPreviewModal = false;
            this.previewFilePath = null;
            this.previewFileName = null;
            this.previewFileType = null;
            this.previewUrl = null;
            this.previewHtml = null;
            this.previewError = null;
            this.useMammothFallback = false;
        },
        async convertDocxToHtml(filePath) {
            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation GetSurveyFileContent($path: String!) {
                            getSurveyFileContent(path: $path) {
                                error
                                error_code
                                message
                                data {
                                    content
                                    file_type
                                }
                            }
                        }
                    `,
                    variables: {
                        path: filePath
                    }
                });

                if (response.data.getSurveyFileContent.error) {
                    throw new Error(response.data.getSurveyFileContent.message);
                }

                const base64Content = response.data.getSurveyFileContent.data.content;
                const binaryString = atob(base64Content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                const result = await mammoth.convertToHtml({ arrayBuffer: bytes.buffer });

                return {
                    html: result.value,
                    messages: result.messages
                };
            } catch (error) {
                console.error('Error converting DOCX to HTML:', error);
                throw error;
            }
        },
        async downloadSurveyFile(path, filename) {
            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation DownloadSurveyFile($path: String!) {
                            downloadSurveyFile(path: $path) {
                                error
                                error_code
                                message
                                data {
                                    download_url
                                }
                            }
                        }
                    `,
                    variables: {
                        path: path
                    }
                });

                if (response.data.downloadSurveyFile.error || !response.data.downloadSurveyFile.data.download_url) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Download Failed',
                        text: response.data.downloadSurveyFile.message || 'File URL not available.',
                    });
                    return;
                }

                const downloadUrl = response.data.downloadSurveyFile.data.download_url;

                try {
                    const contentResponse = await this.$apollo.mutate({
                        mutation: gql`
                            mutation GetSurveyFileContent($path: String!) {
                                getSurveyFileContent(path: $path) {
                                    error
                                    error_code
                                    message
                                    data {
                                        content
                                        file_type
                                    }
                                }
                            }
                        `,
                        variables: {
                            path: path
                        }
                    });

                    if (!contentResponse.data.getSurveyFileContent.error) {
                        const base64Content = contentResponse.data.getSurveyFileContent.data.content;
                        const fileType = contentResponse.data.getSurveyFileContent.data.file_type;
                        const binaryString = atob(base64Content);
                        const bytes = new Uint8Array(binaryString.length);
                        for (let i = 0; i < binaryString.length; i++) {
                            bytes[i] = binaryString.charCodeAt(i);
                        }
                        const blob = new Blob([bytes], { type: fileType });
                        const blobUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = filename || 'download';
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                    } else {
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        link.download = filename || 'download';
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                } catch (error) {
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download = filename || 'download';
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } catch (error) {
                console.error('Error downloading file:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Download Failed',
                    text: 'Failed to download the file. Please try again.',
                });
            }
        },
        getFileIcon(fileType) {
            const iconMap = {
                'application/pdf': 'file-pdf',
                'application/msword': 'file-word',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-word',
                'application/vnd.ms-excel': 'file-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'file-excel',
                'application/vnd.ms-powerpoint': 'file-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'file-powerpoint',
                'text/plain': 'file-alt',
                'image/jpeg': 'file-image',
                'image/jpg': 'file-image',
                'image/png': 'file-image',
                'image/gif': 'file-image',
                'image/webp': 'file-image',
            };
            return iconMap[fileType] || 'file';
        },
        getFileIconColor(fileType) {
            const colorMap = {
                'application/pdf': 'text-red-600',
                'application/msword': 'text-blue-600',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'text-blue-600',
                'application/vnd.ms-excel': 'text-green-600',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'text-green-600',
                'application/vnd.ms-powerpoint': 'text-orange-600',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'text-orange-600',
                'text/plain': 'text-gray-600',
                'image/jpeg': 'text-purple-600',
                'image/jpg': 'text-purple-600',
                'image/png': 'text-purple-600',
                'image/gif': 'text-purple-600',
                'image/webp': 'text-purple-600',
            };
            return colorMap[fileType] || 'text-gray-600';
        },
        isImageFile(fileType) {
            const imageTypes = [
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/gif',
                'image/bmp',
                'image/webp',
                'image/svg+xml',
                'image/tiff',
                'image/tif'
            ];
            return imageTypes.includes(fileType);
        },
        isPdfFile(fileType) {
            const pdfTypes = ['application/pdf'];
            return pdfTypes.includes(fileType);
        },
        isTextFile(fileType) {
            const textTypes = [
                'text/plain',
                'text/html',
                'text/css',
                'text/javascript',
                'text/csv',
                'application/json',
                'application/xml',
                'text/xml'
            ];
            return textTypes.includes(fileType);
        },
        isOfficeDocument(fileType) {
            const officeTypes = [
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation'
            ];
            return officeTypes.includes(fileType);
        },
        isDocxFile(fileType) {
            const docxTypes = [
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ];
            return docxTypes.includes(fileType);
        },
        downloadSignature(dataURL, questionText) {
            try {
                if (!dataURL) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Download Failed',
                        text: 'Signature data not available.',
                    });
                    return;
                }

                // Convert base64 data URL to blob
                const byteString = atob(dataURL.split(',')[1]);
                const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }
                const blob = new Blob([ab], { type: mimeString });

                // Create download link
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                
                // Generate filename from question text
                const sanitizedQuestionText = questionText 
                    ? questionText.replace(/[^a-z0-9]/gi, '_').toLowerCase().substring(0, 30)
                    : 'signature';
                link.download = `${sanitizedQuestionText}_signature.png`;
                
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Clean up the object URL
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error downloading signature:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Download Failed',
                    text: 'Failed to download the signature. Please try again.',
                });
            }
        },
        getEmojiRatingSvg(value) {
            if (!value) return '';
            const strValue = String(value);
            const option = this.emojiRatingOptions[strValue];
            return option ? option.svg : '';
        },
        getEmojiRatingLabel(value) {
            if (!value) return '';
            const strValue = String(value);
            const option = this.emojiRatingOptions[strValue];
            return option ? option.label : '';
        },
        formatDate(date) {
            if (!date) return '';
            return new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
    },
};
</script>

<style lang="scss" scoped>
/* Rich Text Description Styles */
.survey-description {
    :deep(p) {
        margin-bottom: 0.75rem;
    }

    :deep(p:last-child) {
        margin-bottom: 0;
    }

    :deep(ul),
    :deep(ol) {
        margin-left: 1.5rem;
        margin-bottom: 0.75rem;
    }

    :deep(li) {
        margin-bottom: 0.25rem;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
        font-weight: 600;
        margin-top: 1rem;
        margin-bottom: 0.5rem;
    }

    :deep(a) {
        color: inherit;
        text-decoration: underline;
    }

    :deep(strong) {
        font-weight: 600;
    }

    :deep(em) {
        font-style: italic;
    }

    :deep(img) {
        max-width: 100%;
        height: auto;
        border-radius: 0.5rem;
        margin: 0.75rem 0;
    }

    :deep(blockquote) {
        border-left: 4px solid rgba(255, 255, 255, 0.5);
        padding-left: 1rem;
        margin: 1rem 0;
        font-style: italic;
        opacity: 0.9;
    }
}
</style>
